/**
 * Tooltip 排序模块。
 * 负责封装 tooltip 参数的排序策略，禁止在此文件中引入渲染或数据结构转换逻辑。
 * 若需新增排序策略或优化现有算法，请在此文件扩展。
 */
import {
  IFlatSortOptions,
  IGroupedAdvancedSortOptions,
  IGroupedSortOptions,
  createSortFunctionByType,
} from '@/pages/datalab/utils/sort';
import {
  ITooltipParam,
  ITooltipSortOptions,
} from '@customComp/Chart/chartConfigs/types';

interface ITooltipSortConfig {
  metricNames?: string[];
  compareDateCount?: number;
  isOnlyOneDimension: boolean;
  stackedDynamicState?: boolean;
}

const normalizeTooltipMetricValue = (
  value: string | number | null | undefined,
): {
  numeric: number;
  isValid: boolean;
} => {
  if (value === '' || value === null || value === undefined) {
    return { numeric: Number.NEGATIVE_INFINITY, isValid: false };
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return { numeric: Number.NEGATIVE_INFINITY, isValid: false };
  }
  return { numeric, isValid: true };
};

export const sortTooltipParams = (
  params: ITooltipParam[],
  options?: ITooltipSortOptions,
): ITooltipParam[] => {
  if (!Array.isArray(params)) {
    console.error('sortTooltipParams expects an array, received:', typeof params);
    return [];
  }
  if (params.length <= 1) return params;

  const { primaryMetricName, metricNames } = options || {};
  const targetMetricName = primaryMetricName || metricNames?.[0] || params[0]?.data?.metricName;
  if (!targetMetricName) return params;

  const buildGroupKey = (item: ITooltipParam): string => `${item?.data?.dimensions || ''}`;

  type TGroup = {
    params: ITooltipParam[];
    primaryValue: number;
    order: number;
    hasValidValue: boolean;
  };

  const groups: TGroup[] = [];
  const groupIndexMap = new Map<string, number>();

  params.forEach((item, index) => {
    const key = buildGroupKey(item);
    let groupIndex = groupIndexMap.get(key);
    if (groupIndex === undefined) {
      groupIndex = groups.length;
      groups.push({
        params: [],
        primaryValue: Number.NEGATIVE_INFINITY,
        order: index,
        hasValidValue: false,
      });
      groupIndexMap.set(key, groupIndex);
    }
    const group = groups[groupIndex];
    group.params.push(item);
    if (!group.hasValidValue && item?.data?.metric === targetMetricName) {
      const { numeric, isValid } = normalizeTooltipMetricValue(item?.data?.value);
      group.primaryValue = numeric;
      group.hasValidValue = isValid;
    }
  });

  const sortedGroups = groups.sort((a, b) => {
    if (a.primaryValue === b.primaryValue) {
      return a.order - b.order;
    }
    return b.primaryValue - a.primaryValue;
  });

  return sortedGroups.reduce<ITooltipParam[]>((acc, group) => acc.concat(group.params), []);
};

type TFlatDecorated = {
  item: ITooltipParam;
  numeric: number;
  hasValidValue: boolean;
  order: number;
};

type TGroupedDecorated = {
  primaryValue: number;
  order: number;
  selectedItem?: ITooltipParam;
};

const parseTooltipSortTarget = (
  target: TFlatDecorated | TGroupedDecorated,
): { isOthers: boolean; value: number; order: number; } => {
  const selectedData = (target as any)?.selectedItem?.data;
  const itemData = (target as any)?.item?.data;
  const isOthersFlag = Boolean(selectedData?.isOthers ?? itemData?.isOthers);

  const value =
    (target as TGroupedDecorated).primaryValue ??
    (target as TFlatDecorated).numeric ??
    Number.NEGATIVE_INFINITY;

  const order =
    (target as TGroupedDecorated).order ??
    (target as TFlatDecorated).order ??
    0;
  return { isOthers: isOthersFlag, value, order };
};

/**
 * 使“其他”分组置顶，其余保持主值降序（数值相同时按原顺序）。
 */
export const sortWithOthers = (
  a: TFlatDecorated | TGroupedDecorated,
  b: TFlatDecorated | TGroupedDecorated,
): number => {
  const aInfo = parseTooltipSortTarget(a);
  const bInfo = parseTooltipSortTarget(b);
  
  if (aInfo.isOthers && !bInfo.isOthers) return 1;
  if (!aInfo.isOthers && bInfo.isOthers) return -1;

  if (aInfo.value === bInfo.value) {
    return aInfo.order - bInfo.order;
  }
  return bInfo.value - aInfo.value;
};

export const createTooltipSortFunction = (
  config: ITooltipSortConfig,
): ((params: ITooltipParam[]) => ITooltipParam[]) => {
  const { metricNames = [], compareDateCount = 0, isOnlyOneDimension, stackedDynamicState } = config;

  // 一个维度不需要排序
  if (isOnlyOneDimension || metricNames.length === 0) {
    return (params: ITooltipParam[]): ITooltipParam[] => params;
  }

  const primaryMetricName = metricNames[0]?.replace(/(t[0-2]_)/, '') || '';
  if (!primaryMetricName) {
    return (params: ITooltipParam[]): ITooltipParam[] => params;
  }

  const buildGroupKey = (item: ITooltipParam): string => item?.data?.dimensions || '';

  let sortOptions:
  | IFlatSortOptions<ITooltipParam>
  | IGroupedSortOptions<ITooltipParam>
  | IGroupedAdvancedSortOptions<ITooltipParam>;

  if (compareDateCount > 0) {
    sortOptions = {
      type: 'grouped',
      primaryKey: () => primaryMetricName,
      groupKey: buildGroupKey,
      keyExtractor: (item) => item?.data?.metricName,
      valueExtractor: (item) => item?.data?.value,
      compareFn: stackedDynamicState ? sortWithOthers : undefined,
    };
  } else if (metricNames.length === 1) {
    sortOptions = {
      type: 'flat',
      primaryKey: () => primaryMetricName,
      keyExtractor: (item) => item?.data?.metricName,
      valueExtractor: (item) => item?.data?.value,
      compareFn: stackedDynamicState ? sortWithOthers : undefined,
    };
  } else {
    sortOptions = {
      type: 'grouped',
      primaryKey: () => primaryMetricName,
      groupKey: buildGroupKey,
      keyExtractor: (item) => item?.data?.metricName,
      valueExtractor: (item) => item?.data?.value,
      compareFn: stackedDynamicState ? sortWithOthers : undefined,
    };
  }
  return createSortFunctionByType<ITooltipParam>(sortOptions);
};


